import { Fragment } from 'react';
import { Link } from 'react-router-dom';

import { getNestedValue } from 'lib/helpers/exportPdf/lib/extractDataFromColDefs';
import {
  camelToSpaceCase,
  formatLargeNumber,
  ia,
  isPrimitiveValue,
  mString,
  snakeToTitleCase
} from 'lib/helpers/utility';

const nonFormattedFields = ['_id'];

export const generateHeaderName = (key) => {
  return camelToSpaceCase(snakeToTitleCase(key.replace(/__/g, '/').replace(/_id/g, ' #')));
};

export const generateColumnDefs = (data) => {
  if (!ia(data)) return [];

  const keys = Object.keys(data[0]).filter((key) => key !== 'children');

  const columnDefs = keys.map((key) => {
    const isNumber = !isNaN(parseFloat(data[0][key])) && isFinite(data[0][key]);

    const headerName = generateHeaderName(key);

    const colDef = { headerName, field: key };

    // add group row
    if (keys.indexOf(key) === 0 && data.some((item) => ia(item.children))) {
      colDef.cellRenderer = 'agGroupCellRenderer';
    }

    if (data[0][key]?.value !== undefined) {
      colDef.valueGetter = (params) => getNestedValue(params.data[key]?.value); // this fixes stuffs like sorting in ag grid or exporting data

      const cellRenderer = (params) => {
        const value = params.data[key]?.value ?? '-';
        const href = params.data[key]?.href;

        // add link if provided
        if (typeof value !== 'object') {
          if (!href) return value;

          return (
            <Link to={href} target="_blank">
              <span className="text-neutral-900 underline hover:opacity-80">{value}</span>
            </Link>
          );
        }

        const entries = Object.entries(value || {});

        return (
          <>
            {entries.map(([currentKey, currentValue], index) => {
              if (!href) {
                return (
                  <span key={currentKey}>
                    {!currentKey.endsWith('_') && typeof currentValue === 'number'
                      ? mString(currentValue)
                      : currentValue}
                    {index < entries.length - 1 && ' / '}{' '}
                  </span>
                );
              }

              return (
                <Fragment key={currentKey}>
                  <Link to={href?.[currentKey]} target="_blank">
                    <span className="text-neutral-900 underline hover:opacity-80">
                      {currentValue}
                    </span>
                  </Link>
                  {index < entries.length - 1 && ' / '}
                </Fragment>
              );
            })}
          </>
        );
      };

      // avoid missing row expanding
      if (!colDef.cellRenderer) {
        colDef.cellRenderer = cellRenderer;
      } else {
        colDef.cellRendererParams = { innerRenderer: cellRenderer };
      }
    }

    if (isNumber) {
      if (key.endsWith('_')) {
        colDef.valueFormatter = (params) => {
          return formatLargeNumber(params.value);
        };
      } else {
        if (!nonFormattedFields.some((substring) => key.includes(substring))) {
          colDef.valueFormatter = (params) => {
            return mString(params.value);
          };
        }
      }
    } else if (isPrimitiveValue(data[0][key])) {
      // capitalize values
      colDef.valueFormatter = (params) => {
        return camelToSpaceCase(snakeToTitleCase(params?.value?.toString())) || '-';
      };
    } else {
      colDef.valueFormatter = (params) => params.value || '-';
    }

    return colDef;
  });

  return columnDefs;
};
